import { DocumentWorkflowTemplateBadges } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/DocumentWorkflowTemplateTable/DocumentWorkflowTemplateBadges';
import { formatDocumentWorkflowCounterparties } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/formatDocumentWorkflowCounterparties.ts';
import { formatEnumValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/utils/formatEnumValue.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DocumentWorkflowTemplateInfo,
	DocumentWorkflowTemplateType,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { ActionIcon, Badge, Flex, Group, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface GetMasterDocumentsTableColumnsParams {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	classifierOptions: Array<{ value: string; label: string }>;
	onPreview: (documentId: string) => void;
}

const APPLICABILITY_COLUMN_MIN_WIDTH = 220;
const ACTIONS_COLUMN_WIDTH = 80;

export const getMasterDocumentsTableColumns = ({
	tMipManage,
	classifierOptions,
	onPreview,
}: GetMasterDocumentsTableColumnsParams): ExtendedColumnDef<DocumentWorkflowTemplateInfo>[] => [
	createTextColumn({
		headerText: tMipManage('master_documents.header.name'),
		filterText: tMipManage('master_documents.filter.name'),
		columnLabel: tMipManage('master_documents.header.name'),
		accessorKey: 'name',
		enableGlobalFilter: true,
		showFilter: true,
		filterVariant: 'text',
		cell: ({ row }) => (
			<Group gap="xs" wrap="nowrap" w="100%" miw="0">
				<Text
					size="B6Medium"
					style={{
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						display: 'block',
					}}
				>
					{row.original.name}
				</Text>
			</Group>
		),
	}),
	createTextColumn({
		id: 'type',
		headerText: tMipManage('master_documents.header.type'),
		filterText: tMipManage('master_documents.filter.type'),
		columnLabel: tMipManage('master_documents.header.type'),
		accessorFn: (row) => formatEnumValue(row.type),
		enableGlobalFilter: true,
		filterVariant: 'multi-select',
		showFilter: true,
		mantineFilterMultiSelectProps: {
			data: (Object.values(DocumentWorkflowTemplateType) as string[]).map((t) => ({
				value: t,
				label: formatEnumValue(t),
			})),
		},
	}),
	createTextColumn({
		id: 'classifiers',
		headerText: tMipManage('master_documents.header.classification'),
		filterText: tMipManage('master_documents.filter.classification'),
		columnLabel: tMipManage('master_documents.header.classification'),
		showFilter: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: {
			data: classifierOptions,
		},
		accessorFn: (row) => row.classifiers?.length ?? 0,
		sortingFn: (rowA, rowB) =>
			(rowA.original.classifiers?.length ?? 0) - (rowB.original.classifiers?.length ?? 0),
		cell: ({ row }) => {
			const classifiers = row.original.classifiers || [];
			const classifiersCount = classifiers.length;

			if (classifiersCount === 0) {
				return <Text size="B6Medium">{LONG_DASH}</Text>;
			}

			const firstClassifier = classifiers[0];

			return (
				<Group gap="xs" wrap="nowrap">
					<DocumentWorkflowTemplateBadges
						items={[firstClassifier]}
						formatItem={(name: string) => name}
					/>
					{classifiersCount > 1 && (
						<Badge
							size="xs"
							variant="filled"
							color="gray"
							radius="xl"
							sx={{
								backgroundColor: defaultTheme.v3.colors.gray[4],
								color: defaultTheme.v3.colors.dark[4],
								fontWeight: 'normal',
								fontSize: '12px',
							}}
						>
							+{classifiersCount - 1}
						</Badge>
					)}
				</Group>
			);
		},
	}),
	createTextColumn({
		id: 'signatureType',
		headerText: tMipManage('master_documents.header.signature'),
		filterText: tMipManage('master_documents.filter.signature'),
		columnLabel: tMipManage('master_documents.header.signature'),
		accessorFn: (row) => formatEnumValue(row.signatureType),
		enableGlobalFilter: true,
		showFilter: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: {
			data: (Object.values(SignatureType) as string[]).map((type) => ({
				value: type,
				label: formatEnumValue(type),
			})),
		},
	}),
	createTextColumn({
		id: 'counterpartyName',
		headerText: tMipManage('master_documents.header.counterparties'),
		filterText: tMipManage('master_documents.filter.counterparties'),
		columnLabel: tMipManage('master_documents.header.counterparties'),
		accessorFn: (row) => formatDocumentWorkflowCounterparties(row.counterparties, ', '),
		enableColumnFilter: true,
		enableSorting: false,
		showFilter: true,
		filterVariant: 'text',
	}),
	createTextColumn({
		id: 'requiredWitnesses',
		headerText: tMipManage('master_documents.header.witnesses'),
		filterText: tMipManage('master_documents.filter.witnesses'),
		columnLabel: tMipManage('master_documents.header.witnesses'),
		accessorFn: (row) => (row.requiredWitnesses ? 'Yes' : 'No'),
		enableSorting: false,
		enableGlobalFilter: true,
		showFilter: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: {
			data: [
				{ value: 'true', label: 'Yes' },
				{ value: 'false', label: 'No' },
			],
		},
	}),
	createTextColumn({
		id: 'visibleIn',
		headerText: tMipManage('master_documents.header.applicability'),
		filterText: tMipManage('master_documents.filter.applicability'),
		columnLabel: tMipManage('master_documents.header.applicability'),
		minSize: APPLICABILITY_COLUMN_MIN_WIDTH,
		showFilter: false,
		accessorFn: (row) => row.visibleIn?.length ?? 0,
		enableSorting: false,
		cell: ({ row }) => {
			const visibleIn = row.original.visibleIn ?? [];
			const count = visibleIn.length;

			if (count === 0) {
				return <Text size="B6Medium">{LONG_DASH}</Text>;
			}

			const first = visibleIn[0];

			return (
				<Group gap="xs" wrap="nowrap">
					<DocumentWorkflowTemplateBadges
						items={[first]}
						formatItem={(item) => formatEnumValue(item)}
					/>
					{count > 1 && (
						<Badge
							size="xs"
							variant="filled"
							color="gray"
							radius="xl"
							sx={{
								backgroundColor: defaultTheme.v3.colors.gray[4],
								color: defaultTheme.v3.colors.dark[4],
								fontWeight: 'normal',
								fontSize: '12px',
							}}
						>
							+{count - 1}
						</Badge>
					)}
				</Group>
			);
		},
	}),
	{
		id: 'preview',
		header: createSortableTextHeader(tMipManage('master_documents.header.actions'), false),
		enableSorting: false,
		enableColumnFilter: false,
		size: ACTIONS_COLUMN_WIDTH,
		minSize: ACTIONS_COLUMN_WIDTH,
		maxSize: ACTIONS_COLUMN_WIDTH,
		cell: ({ row }) => (
			<Flex justify="flex-start" w="100%">
				<ActionIcon
					size="lg"
					variant="subtle"
					color="blue"
					title={tMipManage('master_documents.text.preview')}
					onClick={(event) => {
						event.stopPropagation();
						onPreview(row.original.id);
					}}
				>
					<Icon variant="eye" size="lg" />
				</ActionIcon>
			</Flex>
		),
	},
];
